import {
  CAMPAIGN_REPLY_LABELS,
  clockMinutes,
  estimateCampaignFinish,
  type CampaignCounts,
  type CampaignExclusion,
  type CampaignOutcome,
  type CampaignRecipient,
  type CampaignRecipientFilter,
  type CampaignRecipientState,
  type CampaignReplyClass,
  type CampaignState,
  type CampaignSummary,
} from "@vekha/contracts";

export type Tone = "safe" | "warning" | "danger" | "neutral" | "info";
type Copy = Readonly<{ label: string; tone: Tone }>;

export const CAMPAIGN_STATE_COPY: Readonly<Record<CampaignState, Copy>> = {
  draft: { label: "Черновик", tone: "neutral" },
  scheduled: { label: "Запланирована", tone: "info" },
  running: { label: "Идёт", tone: "safe" },
  paused: { label: "Пауза", tone: "warning" },
  completed: { label: "Завершена", tone: "info" },
  cancelled: { label: "Остановлена", tone: "neutral" },
};

/** A campaign cancelled before its first message was never «stopped» — it was called off. */
export function campaignStateCopy(campaign: Pick<CampaignSummary, "state" | "startedAt">): Copy {
  if (campaign.state === "cancelled" && !campaign.startedAt) return { label: "Отменена", tone: "neutral" };
  return CAMPAIGN_STATE_COPY[campaign.state];
}

export const RECIPIENT_STATE_COPY: Readonly<Record<CampaignRecipientState, Copy>> = {
  pending: { label: "В очереди", tone: "neutral" },
  generating: { label: "Агент пишет", tone: "info" },
  ready: { label: "Текст готов", tone: "info" },
  sending: { label: "Отправляется", tone: "warning" },
  sent: { label: "Отправлено", tone: "safe" },
  skipped: { label: "Пропущен", tone: "neutral" },
  failed: { label: "Ошибка", tone: "danger" },
  cancelled: { label: "Снят", tone: "neutral" },
};

export const REPLY_TONE: Readonly<Record<CampaignReplyClass, Tone>> = {
  interested: "safe",
  question: "info",
  not_interested: "neutral",
  negative: "danger",
  opt_out: "danger",
};

/** The page's words for an answer: without a person's gender (16.10); notes in amoCRM keep the contract's labels. */
const REPLY_LABELS: Readonly<Record<CampaignReplyClass, string>> = {
  ...CAMPAIGN_REPLY_LABELS, interested: "Интерес", question: "Вопрос",
};

export function replyCopy(reply: CampaignReplyClass): Copy {
  return { label: REPLY_LABELS[reply], tone: REPLY_TONE[reply] };
}

export const OUTCOME_COPY: Readonly<Record<CampaignOutcome, Copy>> = {
  purchased: { label: "Покупка", tone: "safe" },
  returned_to_work: { label: "Снова в работе", tone: "info" },
};

export const EXCLUSION_LABELS: Readonly<Record<CampaignExclusion, string>> = {
  no_dialog: "Нет переписки",
  inactive: "Давно не писали",
  recent_dialog: "Недавно переписывались",
  never_wrote: "Сами нам не писали",
  other_campaign: "Уже в другой рассылке",
  cooldown: "Недавно получали рассылку",
  opt_out: "Просили не писать",
  no_consultation: "Не было консультации",
};

export const RECIPIENT_FILTERS: readonly Readonly<{ key: CampaignRecipientFilter; label: string }>[] = [
  { key: "all", label: "Все" },
  { key: "sent", label: "Отправлено" },
  { key: "replied", label: "Ответили" },
  { key: "interested", label: "Заинтересовались" },
  { key: "purchased", label: "Купили" },
  { key: "no_reply", label: "Без ответа" },
  { key: "read", label: "Прочитали" },
  { key: "waiting", label: "В очереди" },
  { key: "skipped", label: "Пропущены" },
  { key: "failed", label: "Ошибки" },
  { key: "undelivered", label: "Не дошло" },
];

export function isRecipientFilter(value: string | null): value is CampaignRecipientFilter {
  return RECIPIENT_FILTERS.some((item) => item.key === value);
}

/** The count behind each recipient tab; mirrors the server's filter clauses. */
export function recipientFilterCount(filter: CampaignRecipientFilter, counts: CampaignCounts): number {
  switch (filter) {
    case "all": return counts.total;
    case "sent": return counts.sent;
    case "replied": return counts.replied;
    case "interested": return counts.interested + counts.question;
    case "purchased": return counts.purchased;
    case "no_reply": return counts.noReply;
    case "waiting": return waitingCount(counts);
    case "skipped": return counts.skipped + counts.cancelled;
    case "failed": return counts.failed;
    case "delivered": return counts.delivered;
    case "undelivered": return counts.undelivered;
    case "read": return counts.read;
  }
}

/** People the campaign has not written yet (queue, prepared, in flight). */
export function waitingCount(counts: CampaignCounts): number {
  return counts.pending + counts.ready + counts.sending;
}

export function isLiveCampaign(state: CampaignState): boolean {
  return state === "draft" || state === "scheduled" || state === "running" || state === "paused";
}

export function pluralRu(count: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(count) % 100;
  const mod10 = mod100 % 10;
  if (mod100 >= 11 && mod100 <= 19) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export function countRu(count: number, one: string, few: string, many: string): string {
  return `${formatNumber(count)} ${pluralRu(count, one, few, many)}`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("ru-RU").format(value);
}

/** «45%» of a whole; «—» when there is nothing to divide by. */
export function percent(part: number, whole: number): string {
  if (whole <= 0) return "—";
  const value = (part / whole) * 100;
  if (value > 0 && value < 1) return "<1%";
  return `${Math.round(value)}%`;
}

export function share(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.max(0, Math.min(1, part / whole));
}

const MOSCOW = "Europe/Moscow";
const dayKey = new Intl.DateTimeFormat("en-CA", { timeZone: MOSCOW, year: "numeric", month: "2-digit", day: "2-digit" });
const timeFormat = new Intl.DateTimeFormat("ru-RU", { timeZone: MOSCOW, hour: "2-digit", minute: "2-digit" });
const dateFormat = new Intl.DateTimeFormat("ru-RU", { timeZone: MOSCOW, day: "numeric", month: "short" });
const dateYearFormat = new Intl.DateTimeFormat("ru-RU", { timeZone: MOSCOW, day: "numeric", month: "short", year: "numeric" });

function moscowDayDiff(date: Date, now: Date): number {
  const a = Date.parse(dayKey.format(date));
  const b = Date.parse(dayKey.format(now));
  return Math.round((a - b) / 86_400_000);
}

/** «сегодня», «завтра», «вчера» or «29 сент.» in Moscow. */
export function formatMoscowDay(value: string | Date, now: Date = new Date()): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const diff = moscowDayDiff(date, now);
  if (diff === 0) return "сегодня";
  if (diff === 1) return "завтра";
  if (diff === -1) return "вчера";
  const sameYear = dayKey.format(date).slice(0, 4) === dayKey.format(now).slice(0, 4);
  return (sameYear ? dateFormat : dateYearFormat).format(date);
}

/** «сегодня, 14:20» / «29 сент., 14:20» — Moscow time, as the team reads it. */
export function formatMoscow(value: string | Date | null, now: Date = new Date()): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "—";
  return `${formatMoscowDay(date, now)}, ${timeFormat.format(date)}`;
}

export function formatMoscowTime(value: string | Date): string {
  return timeFormat.format(typeof value === "string" ? new Date(value) : value);
}

/** «≈ 4,5 рабочих часа» */
export function formatWorkingHours(hours: number): string {
  if (hours <= 0) return "меньше часа";
  if (hours < 1) return `≈ ${Math.max(1, Math.round(hours * 60))} мин`;
  const rounded = Math.round(hours * 10) / 10;
  const text = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(rounded);
  const noun = Number.isInteger(rounded) ? pluralRu(rounded, "рабочий час", "рабочих часа", "рабочих часов") : "рабочих часа";
  return `≈ ${text} ${noun}`;
}

/** «1 сообщение в 2 мин» for an hourly limit. */
export function paceInterval(hourlyLimit: number): string {
  if (hourlyLimit <= 0) return "—";
  const minutes = 60 / hourlyLimit;
  if (minutes >= 1) {
    const rounded = Math.round(minutes * 10) / 10;
    return `примерно раз в ${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(rounded)} мин`;
  }
  return `примерно раз в ${Math.round(minutes * 60)} сек`;
}

export function paceSummary(pace: CampaignSummary["pace"]): string {
  return `до ${pace.hourlyLimit} в час · ${pace.windowStart}–${pace.windowEnd} МСК`;
}

/** The server's forecast, or a local one for a draft or a scheduled campaign (from its planned start). */
export function campaignFinishEstimate(campaign: CampaignSummary, now: Date = new Date()): string | null {
  if (!isLiveCampaign(campaign.state)) return null;
  if (campaign.estimatedFinishAt) return campaign.estimatedFinishAt;
  const plannedStart = campaign.state === "scheduled" && campaign.scheduledStartAt ? new Date(campaign.scheduledStartAt) : null;
  const from = plannedStart && plannedStart > now ? plannedStart : now;
  const finish = estimateCampaignFinish(waitingCount(campaign.counts), campaign.pace, from);
  return finish ? finish.toISOString() : null;
}

// ——— Scheduling: the owner picks a Moscow wall time; Moscow is UTC+3 all year. ———

const MOSCOW_OFFSET_MS = 3 * 3_600_000;
const DAY_MS = 86_400_000;
export const SCHEDULE_MIN_LEAD_MS = 60_000;
export const SCHEDULE_MAX_DAYS = 90;

export type WallClock = Readonly<{ date: string; time: string }>;

/** «2026-09-29» and «10:00» of a moment, as a Moscow clock shows it. */
export function moscowWallClock(value: Date | string): WallClock {
  const date = typeof value === "string" ? new Date(value) : value;
  const shifted = new Date(date.getTime() + MOSCOW_OFFSET_MS).toISOString();
  return { date: shifted.slice(0, 10), time: shifted.slice(11, 16) };
}

/** A Moscow date and time as an ISO moment with the +03:00 offset; null while the inputs are incomplete. */
export function moscowWallToIso(date: string, time: string): string | null {
  if (!/^20\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/u.test(date)) return null;
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(time)) return null;
  const iso = `${date}T${time}:00+03:00`;
  const parsed = new Date(iso);
  // Reject dates that do not exist (31 Sept rolls over to 1 Oct).
  if (Number.isNaN(parsed.getTime()) || moscowWallClock(parsed).date !== date) return null;
  return iso;
}

function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** The first choice offered: tomorrow at the opening of the campaign window. */
export function defaultScheduleStart(pace: CampaignSummary["pace"], now: Date = new Date()): WallClock {
  return { date: addDays(moscowWallClock(now).date, 1), time: pace.windowStart === "24:00" ? "00:00" : pace.windowStart };
}

type Option = Readonly<{ value: string; label: string }>;

const optionDay = new Intl.DateTimeFormat("ru-RU", { timeZone: "UTC", day: "numeric", month: "short" });
const optionDayYear = new Intl.DateTimeFormat("ru-RU", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" });
const optionWeekday = new Intl.DateTimeFormat("ru-RU", { timeZone: "UTC", weekday: "short" });

/** «Завтра, 29 сент.» / «Ср, 30 сент.» for a Moscow calendar date, as dates read elsewhere in the app. */
export function scheduleDateLabel(date: string, now: Date = new Date()): string {
  const today = moscowWallClock(now).date;
  const noon = new Date(`${date}T12:00:00Z`);
  const day = (date.slice(0, 4) === today.slice(0, 4) ? optionDay : optionDayYear).format(noon);
  if (date === today) return `Сегодня, ${day}`;
  if (date === addDays(today, 1)) return `Завтра, ${day}`;
  const weekday = optionWeekday.format(noon);
  return `${weekday.slice(0, 1).toUpperCase()}${weekday.slice(1)}, ${day}`;
}

/** Every Moscow date a campaign can start on: today … 90 days ahead, plus an already planned one. */
export function scheduleDateOptions(now: Date = new Date(), keep: string | null = null): Option[] {
  const today = moscowWallClock(now).date;
  const dates = Array.from({ length: SCHEDULE_MAX_DAYS + 1 }, (_, index) => addDays(today, index));
  if (keep && !dates.includes(keep)) dates.push(keep);
  return dates.sort().map((value) => ({ value, label: scheduleDateLabel(value, now) }));
}

/** 24-hour times every 15 minutes, plus an already planned time off the grid. */
export function scheduleTimeOptions(keep: string | null = null): Option[] {
  const times = Array.from({ length: 96 }, (_, index) => {
    const minutes = index * 15;
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  });
  if (keep && !times.includes(keep)) times.push(keep);
  return times.sort().map((value) => ({ value, label: value }));
}

/** Why the chosen moment cannot be used, mirroring the server (1 minute … 90 days ahead). */
export function scheduleProblem(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return "Укажите дату и время старта.";
  const lead = Date.parse(iso) - now.getTime();
  if (lead < SCHEDULE_MIN_LEAD_MS) return "Это время уже прошло — выберите момент хотя бы на минуту позже.";
  if (lead > SCHEDULE_MAX_DAYS * DAY_MS) return `Запланировать можно не дальше чем на ${SCHEDULE_MAX_DAYS} дней вперёд.`;
  return null;
}

/** Messages go only inside the daily window; a start outside it waits for the window to open. */
export function scheduleWindowNote(time: string, pace: CampaignSummary["pace"]): string | null {
  if (!/^\d{2}:\d{2}$/u.test(time)) return null;
  const minute = clockMinutes(time);
  if (minute < clockMinutes(pace.windowStart)) return `До ${pace.windowStart} сообщения не уходят — первые пойдут с ${pace.windowStart}.`;
  if (minute >= clockMinutes(pace.windowEnd)) return `Окно рассылки закрывается в ${pace.windowEnd} — сообщения пойдут на следующий день с ${pace.windowStart}.`;
  return null;
}

export function agentInitial(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || "А";
}

export function recipientName(recipient: Pick<CampaignRecipient, "contactName" | "dealName" | "dealExternalId">): string {
  return recipient.contactName?.trim() || recipient.dealName?.trim() || `Сделка ${recipient.dealExternalId}`;
}

type MessageLike = Readonly<{ state: string; deliveryStatus: string | null; deliveryError?: string | null | undefined }>;

/**
 * «Не дошло» (Ден 17.10.2026, «получили» — by delivery): the channel refused every sent message whose delivery it reported —
 * the server's rule (campaigns/service.ts REFUSED_DELIVERY), on one recipient's bubbles.
 */
export function isRefused(recipient: Readonly<{ messages: readonly MessageLike[] }>): boolean {
  const known = recipient.messages.filter((message) => message.deliveryStatus);
  return known.length > 0 && known.every((message) => message.deliveryStatus === "error");
}

/** «Прочитали» of one recipient, as the server counts it: delivered, and a message read by the channel's mark or answered. */
export function isRead(recipient: Readonly<{ messages: readonly MessageLike[]; repliedAt?: string | null; sentAt?: string | null }>): boolean {
  if (!recipient.sentAt || isRefused(recipient)) return false;
  return Boolean(recipient.repliedAt) || recipient.messages.some((message) => message.deliveryStatus === "read");
}

/** WhatsApp shows no read mark to people who turned read receipts off: «Прочитали» can only be lower than the truth. */
export const READ_HINT = "Прочитали — канал показал отметку о прочтении или человек ответил. В WhatsApp отметку можно выключить, поэтому прочитавших может быть больше.";

/** Why a message did not reach, in a few words (the channel's code stays in the hint). */
export function undeliveredReason(code: string | null | undefined): string {
  switch (code) {
    case "SPAM": return "Telegram не дал написать первым";
    case "INPUT_USER_DEACTIVATED": return "аккаунт Telegram удалён";
    case "BAD_CONTACT": return "номера нет в WhatsApp";
    case "CHANNEL_TGAPI_PHONE_NOT_OCCUPIED": return "по номеру нет Telegram";
    case "CHANNEL_TGAPI_USERNAME_NOT_OCCUPIED": return "ник Telegram не найден";
    default: return "канал отклонил";
  }
}

/** One bubble's delivery as the operator reads it. */
export function messageDelivery(message: MessageLike): Copy {
  switch (message.deliveryStatus) {
    case "read": return { label: "Прочитано", tone: "safe" };
    case "delivered": return { label: "Доставлено", tone: "safe" };
    case "sent": return { label: "Отправлено", tone: "safe" };
    case "error": return { label: message.deliveryError ? `Не дошло: ${undeliveredReason(message.deliveryError)}` : "Не дошло", tone: "danger" };
    default: break;
  }
  switch (message.state) {
    case "confirmed": return { label: "Отправлено", tone: "safe" };
    case "dispatching": return { label: "Отправляется", tone: "warning" };
    case "ambiguous": return { label: "Не подтверждено", tone: "warning" };
    case "terminal_failed": return { label: "Не отправлено", tone: "danger" };
    default: return { label: "Ждёт отправки", tone: "neutral" };
  }
}
